import { Fragment, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BarChart3, CalendarDays, CreditCard, Download, Edit3, Loader2, Lock, Mail, MailX, Receipt, RotateCcw, Save, ScanLine, Send, Ticket, Trash2, TrendingUp, Users, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatEventDate, formatPrice } from "@/components/site/EventCard";
import { API_BASE } from "@/lib/supabase";
import SalesChart from "@/components/admin/SalesChart";

const ORDER_CUSTOMER_FIELDS = [
  ["customer_first_name", "Voornaam"],
  ["customer_last_name", "Achternaam"],
  ["customer_email", "E-mailadres"],
  ["customer_phone", "Telefoon"],
  ["customer_company_name", "Bedrijfsnaam"],
  ["customer_street", "Straat + huisnummer"],
  ["customer_postal_code", "Postcode"],
  ["customer_city", "Plaats"],
];

// A field with depends_on_timeslot_category_id only shows once the ticket's
// OWN booked category matches; depends_on_field_id works the same way
// Checkout.jsx's own fieldIsVisible does. Without this, every category's own
// custom fields showed on every ticket regardless of which category it was
// actually booked under -- the exact "dubbele velden" report (two
// categories' near-identical fields, e.g. both named "Naam kind of namen
// kinderen", both shown unfiltered side by side).
function orderEditFieldIsVisible(field, values, fieldsById, categoryId) {
  if (field.depends_on_timeslot_category_id) {
    return field.depends_on_timeslot_category_id === categoryId;
  }
  if (!field.depends_on_field_id) return true;
  const controller = fieldsById[field.depends_on_field_id];
  if (!controller) return true;
  const actual = String(values?.[controller.key] ?? "").trim().toLowerCase();
  const trigger = String(field.depends_on_value ?? "").trim().toLowerCase();
  return actual === trigger;
}

// A ticket's own admin-defined custom field answers (multi-day registration /
// timeslot-booking events) -- a small local re-implementation of
// Checkout.jsx's RegistrationFieldInput (that file isn't a shared module),
// shaped the same way: text/textarea/select/checkbox per field_type.
function TicketFieldInput({ field, value, onChange }) {
  const cls = "mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan";
  if (field.field_type === "textarea") {
    return <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} className={`${cls} h-auto`} />;
  }
  if (field.field_type === "select") {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls}>
        <option value="">Kies…</option>
        {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }
  if (field.field_type === "checkbox") {
    return (
      <label className="mt-1 flex items-center gap-2">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
        <span className="text-xs text-slate-300">Ja</span>
      </label>
    );
  }
  const inputType = field.field_type === "number" ? "number" : field.field_type === "email" ? "email" : field.field_type === "phone" ? "tel" : "text";
  return <input type={inputType} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} />;
}

function TicketEditRow({ ticket, event, registrationFields, routeGroups, timeslots, timeslotCategories, authAxios, onChanged }) {
  const [form, setForm] = useState({
    holder_name: ticket.holder_name || "",
    route_group_id: ticket.route_group_id || "",
    medal_number: ticket.medal_number || "",
    start_number: ticket.start_number != null ? String(ticket.start_number) : "",
    field_values: { ...(ticket.registration_field_values || {}) },
  });
  const [saving, setSaving] = useState(false);
  const [swapping, setSwapping] = useState(false);

  // The field admins flagged "Dit veld bevat de naam van de deelnemer" is
  // auto-filled from the ticket's own holder name and shown above as "Naam" —
  // rendering it a second time as its own custom field would just duplicate
  // the same name under two labels. Its value is kept in sync with "Naam" on
  // save instead of being shown/edited separately.
  const participantNameField = registrationFields.find((f) => f.is_participant_name);
  // The ticket's own booked category (if any), resolved from its booked
  // slot(s) -- needed so a category-conditional field only shows for the
  // category this ticket actually belongs to, not every category's fields
  // at once.
  const ticketCategoryId = (ticket.timeslot_ids || [])
    .map((id) => timeslots.find((s) => s.id === id))
    .find(Boolean)?.category_id;
  const fieldsById = Object.fromEntries(registrationFields.map((f) => [f.id, f]));
  const visibleFields = registrationFields.filter(
    (f) => !f.is_participant_name && orderEditFieldIsVisible(f, form.field_values, fieldsById, ticketCategoryId),
  );

  const save = async () => {
    setSaving(true);
    try {
      const fieldValues = { ...form.field_values };
      if (participantNameField) {
        fieldValues[participantNameField.key] = form.holder_name;
      }
      const payload = {
        holder_name: form.holder_name,
        registration_field_values: fieldValues,
      };
      if (event?.companion_app_enabled) {
        payload.route_group_id = form.route_group_id || null;
        payload.medal_number = form.medal_number || null;
      }
      if (event?.start_numbers_enabled) {
        payload.start_number = form.start_number.trim() ? Number(form.start_number) : null;
      }
      await authAxios.patch(`/admin/tickets/${ticket.id}/participant`, payload);
      toast.success("Ticket bijgewerkt");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-svj-border bg-svj-bg/50 p-3 space-y-2" data-testid={`order-edit-ticket-${ticket.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-slate-400">
        <span className="font-mono">{ticket.code}</span>
        {ticket.timeslot_label && <span>{ticket.timeslot_label}</span>}
        {(ticket.registered_days || []).length > 0 && <span>{ticket.registered_days.join(", ")}</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs text-slate-400">Naam</span>
          <input
            value={form.holder_name}
            onChange={(e) => setForm({ ...form, holder_name: e.target.value })}
            className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
        {event?.companion_app_enabled && routeGroups.length > 0 && (
          <label className="block">
            <span className="text-xs text-slate-400">Routekeuze</span>
            <select
              value={form.route_group_id}
              onChange={(e) => setForm({ ...form, route_group_id: e.target.value })}
              className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            >
              <option value="">Geen</option>
              {routeGroups.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.distance_km} km)</option>)}
            </select>
          </label>
        )}
        {event?.companion_app_enabled && (
          <label className="block">
            <span className="text-xs text-slate-400">Medaillenummer</span>
            <input
              value={form.medal_number}
              onChange={(e) => setForm({ ...form, medal_number: e.target.value })}
              className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            />
          </label>
        )}
        {event?.start_numbers_enabled && (
          <label className="block">
            <span className="text-xs text-slate-400">Startnummer</span>
            <input
              type="number"
              value={form.start_number}
              onChange={(e) => setForm({ ...form, start_number: e.target.value })}
              className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            />
          </label>
        )}
      </div>
      {visibleFields.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {visibleFields.map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs text-slate-400">{f.label}</span>
              <TicketFieldInput
                field={f}
                value={form.field_values[f.key]}
                onChange={(v) => setForm({ ...form, field_values: { ...form.field_values, [f.key]: v } })}
              />
            </label>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        {event?.timeslot_booking_enabled && (
          <button
            type="button"
            onClick={() => setSwapping((s) => !s)}
            data-testid={`order-edit-ticket-swap-toggle-${ticket.id}`}
            className="text-xs font-semibold text-svj-cyan hover:underline"
          >
            Tijdslot wijzigen
          </button>
        )}
        <div className="flex-1" />
        <button type="button" onClick={save} disabled={saving} data-testid={`order-edit-ticket-save-${ticket.id}`} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
        </button>
      </div>
      {swapping && event?.timeslot_booking_enabled && (
        <TicketTimeslotSwap
          ticket={ticket}
          timeslots={timeslots || []}
          categories={timeslotCategories || []}
          authAxios={authAxios}
          onDone={() => {
            setSwapping(false);
            onChanged();
          }}
          onCancel={() => setSwapping(false)}
        />
      )}
    </div>
  );
}

// Reuses the exact PATCH /admin/tickets/{ticket_id}/timeslot endpoint
// TimeslotSchedule.jsx's own "Wissel" SwapPanel already uses -- same
// single-target-slot scope (a multi-slot booking can be moved one slot at a
// time, same reasoning as SwapPanel's own docstring), same notify/message
// options. Kept as a second, small implementation rather than importing
// SwapPanel itself: that component expects a "slot" shaped like a
// TimeslotSchedule row (with its own .booking), not a ticket.
function TicketTimeslotSwap({ ticket, timeslots, categories, authAxios, onDone, onCancel }) {
  const currentIds = new Set(ticket.timeslot_ids || []);
  // Default to the ticket's own current category, so re-opening this panel
  // doesn't make the admin re-pick a category they already chose at booking
  // time -- only relevant when the event actually has categories at all.
  const currentCategoryId = timeslots.find((s) => currentIds.has(s.id))?.category_id;
  const [categoryId, setCategoryId] = useState(currentCategoryId || (categories[0]?.id ?? ""));
  const [targetId, setTargetId] = useState("");
  const [notify, setNotify] = useState(true);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const relevantSlots = categories.length === 0 ? timeslots : timeslots.filter((s) => s.category_id === categoryId);
  const byDate = relevantSlots
    .filter((s) => !currentIds.has(s.id) && s.active !== false)
    .reduce((acc, s) => {
      (acc[s.date] = acc[s.date] || []).push(s);
      return acc;
    }, {});
  const dates = Object.keys(byDate).sort();

  const submit = async () => {
    if (!targetId) {
      toast.error("Kies een nieuw tijdslot");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await authAxios.patch(`/admin/tickets/${ticket.id}/timeslot`, {
        timeslot_ids: [targetId],
        notify_customer: notify,
        message: message.trim() || null,
      });
      if (notify && data.email && !data.email.sent) {
        toast.error(`Tijdslot gewisseld, maar e-mail is niet gelukt: ${data.email.error || "onbekende fout"}`);
      } else {
        toast.success(notify ? "Tijdslot gewisseld en klant gemaild" : "Tijdslot gewisseld");
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-3 py-3 rounded-lg bg-svj-bg border border-svj-cyan/40 space-y-2" data-testid={`order-edit-ticket-swap-${ticket.id}`}>
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { setCategoryId(c.id); setTargetId(""); }}
              data-testid={`order-edit-ticket-swap-category-${ticket.id}-${c.id}`}
              className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
                categoryId === c.id ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan" : "bg-svj-card border-svj-border text-slate-300 hover:text-white"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {dates.length === 0 && <div className="text-xs text-slate-500">Geen andere tijdsloten beschikbaar voor deze categorie.</div>}
        {dates.map((date) => (
          <div key={date}>
            <div className="text-[11px] text-slate-400 mb-1">
              {new Date(`${date}T00:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {byDate[date]
                .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
                .map((s) => {
                  const full = s.full ?? (s.booked_count || 0) >= s.capacity;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => !full && setTargetId(s.id)}
                      disabled={full}
                      data-testid={`order-edit-ticket-swap-target-${s.id}`}
                      className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
                        targetId === s.id
                          ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                          : full
                            ? "bg-svj-bg border-svj-border text-slate-600 cursor-not-allowed opacity-60"
                            : "bg-svj-card border-svj-border text-slate-300 hover:text-white"
                      }`}
                    >
                      {(s.start_time || "").slice(0, 5)} - {(s.end_time || "").slice(0, 5)}
                      {full && <span className="ml-1 text-[9px] uppercase text-red-400">vol</span>}
                    </button>
                  );
                })}
            </div>
          </div>
        ))}
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-300">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-3.5 w-3.5 rounded border-svj-border bg-svj-bg" />
        Stuur e-mail naar klant over de wijziging
      </label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Optionele notitie voor de klant…"
        rows={2}
        className="w-full px-3 py-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
      />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="text-xs font-semibold text-slate-400 hover:text-white px-3 py-1.5">
          Annuleren
        </button>
        <button type="button" onClick={submit} disabled={submitting} data-testid={`order-edit-ticket-swap-submit-${ticket.id}`} className="rounded-full bg-svj-cyan text-svj-bg font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Wissel
        </button>
      </div>
    </div>
  );
}

// Full order-editing panel -- expanded inline below an order row. Fetches its
// own detail (customer fields, order lines, finalized tickets if any) and
// exposes every post-purchase correction an admin can make: edit buyer/
// contact data, edit each ticket's own custom field answers, resend the
// confirmation e-mail, (re)send a payment link to complete a stuck/unpaid
// order, or mark an order paid directly when it was settled outside Pay.nl/
// Mollie (e.g. a bank transfer).
function OrderEditPanel({ orderId, authAxios, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", pay_status: "" });
  const [timeslots, setTimeslots] = useState([]);
  const [timeslotCategories, setTimeslotCategories] = useState([]);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [reservingPending, setReservingPending] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [resending, setResending] = useState(false);
  const [deletingTickets, setDeletingTickets] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/orders/${orderId}`);
      setDetail(data);
      setForm(Object.fromEntries(ORDER_CUSTOMER_FIELDS.map(([k]) => [k, data.order?.[k] || ""])));
      setStatusForm({ status: data.order?.status || "", pay_status: data.order?.pay_status || "" });
      if (data.event?.timeslot_booking_enabled && data.event?.id) {
        try {
          const [{ data: slotData }, { data: categoryData }] = await Promise.all([
            authAxios.get(`/admin/events/${data.event.id}/timeslots`),
            authAxios.get(`/admin/events/${data.event.id}/timeslot-categories`),
          ]);
          setTimeslots(slotData.items || []);
          setTimeslotCategories((categoryData.items || []).filter((c) => c.active));
        } catch {
          setTimeslots([]);
          setTimeslotCategories([]);
        }
      } else {
        setTimeslots([]);
        setTimeslotCategories([]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const saveCustomer = async () => {
    setSavingCustomer(true);
    try {
      await authAxios.patch(`/admin/orders/${orderId}`, form);
      toast.success("Klantgegevens opgeslagen");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSavingCustomer(false);
    }
  };

  const saveStatus = async () => {
    setSavingStatus(true);
    try {
      await authAxios.patch(`/admin/orders/${orderId}/status`, statusForm);
      toast.success("Betaalstatus opgeslagen");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSavingStatus(false);
    }
  };

  const sendPaymentLink = async () => {
    setSendingLink(true);
    try {
      await authAxios.post(`/admin/orders/${orderId}/send-payment-link`);
      toast.success("Betaallink verstuurd");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSendingLink(false);
    }
  };

  const reservePending = async () => {
    setReservingPending(true);
    try {
      await authAxios.post(`/admin/orders/${orderId}/reserve-pending`);
      toast.success("Plek weer geblokkeerd — betaallink nog niet verstuurd");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setReservingPending(false);
    }
  };

  const markPaid = async () => {
    if (!window.confirm("Deze bestelling markeren als betaald zonder een echte betaling via de betaalprovider? De klant ontvangt direct de ticket-bevestigingsmail.")) return;
    setMarkingPaid(true);
    try {
      await authAxios.post(`/admin/orders/${orderId}/mark-paid`);
      toast.success("Bestelling gemarkeerd als betaald");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setMarkingPaid(false);
    }
  };

  const resendEmail = async () => {
    setResending(true);
    try {
      await authAxios.post(`/admin/orders/${orderId}/resend-email`);
      toast.success("E-mail opnieuw verzonden");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setResending(false);
    }
  };

  const deleteTickets = async () => {
    if (!window.confirm("Weet je zeker dat je de tickets van deze bestelling wilt verwijderen? De bestelling zelf blijft bestaan, maar wordt teruggezet naar 'in behandeling' zodat je 'm opnieuw kunt afhandelen (bv. een betaallink sturen naar een echte klant). Dit kan niet ongedaan worden gemaakt.")) return;
    setDeletingTickets(true);
    try {
      await authAxios.post(`/admin/orders/${orderId}/delete-tickets`);
      toast.success("Tickets verwijderd — bestelling is weer open voor afhandeling");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setDeletingTickets(false);
    }
  };

  if (loading || !detail || !form) {
    return (
      <div className="px-5 py-6 bg-svj-bg/30 border-t border-svj-border" data-testid={`order-edit-panel-${orderId}`}>
        <Loader2 className="h-5 w-5 animate-spin text-svj-cyan" />
      </div>
    );
  }

  const { order, event, tickets, registration_fields: registrationFields, route_groups: routeGroups } = detail;
  const isPaid = !!order.paid;
  const isCancelled = order.status === "cancelled";
  const canSendPaymentLink = !isPaid && !isCancelled && (tickets || []).length === 0 && (order.total_cents || 0) > 0;
  const canReservePending = canSendPaymentLink && !["reserved", "payment_pending"].includes(order.status);
  const canMarkPaid = !isPaid && !isCancelled;
  const canDeleteTickets = !isPaid && !isCancelled && (tickets || []).length > 0;

  return (
    <div className="px-5 py-5 bg-svj-bg/30 border-t border-svj-border space-y-4" data-testid={`order-edit-panel-${orderId}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Bestelling bewerken</div>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Klantgegevens</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ORDER_CUSTOMER_FIELDS.map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-xs text-slate-400">{label}</span>
              <input
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`order-edit-${key}`}
                className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
              />
            </label>
          ))}
        </div>
        <div className="mt-2 flex justify-end">
          <button type="button" onClick={saveCustomer} disabled={savingCustomer} data-testid="order-edit-save-customer" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
            {savingCustomer ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
          </button>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Betaalstatus</div>
        <div className="mb-2 text-[11px] text-amber-300/90">
          Dit wijzigt alleen het statuslabel van de bestelling — er worden geen tickets aangemaakt, gewijzigd of
          verwijderd en er wordt geen e-mail verstuurd. Gebruik "Markeer als betaald"/"Betaallink versturen" hieronder
          voor een echte afhandeling.
          {(tickets || []).length > 0 && (
            <>
              {" "}Let op: "Betaallink versturen" verschijnt pas zodra deze bestelling géén tickets meer heeft — zie
              "Verwijder tickets" hieronder als je deze bestelling (bv. een testbestelling die je handmatig op
              betaald had gezet) opnieuw wilt afhandelen voor een echte klant.
            </>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs text-slate-400">Status</span>
            <select
              value={statusForm.status}
              onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
              data-testid="order-edit-status"
              className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            >
              <option value="created">In behandeling (created)</option>
              <option value="paid">Betaald (paid)</option>
              <option value="failed">Mislukt (failed)</option>
              <option value="cancelled">Geannuleerd (cancelled)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Pay status (vrij label)</span>
            <input
              value={statusForm.pay_status}
              onChange={(e) => setStatusForm({ ...statusForm, pay_status: e.target.value })}
              data-testid="order-edit-pay-status"
              className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            />
          </label>
        </div>
        <div className="mt-2 flex justify-end">
          <button type="button" onClick={saveStatus} disabled={savingStatus} data-testid="order-edit-save-status" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
            {savingStatus ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
          </button>
        </div>
      </div>

      {(tickets || []).length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Tickets</div>
          <div className="space-y-2">
            {tickets.map((t) => (
              <TicketEditRow
                key={t.id}
                ticket={t}
                event={event}
                registrationFields={registrationFields || []}
                routeGroups={routeGroups || []}
                timeslots={timeslots}
                timeslotCategories={timeslotCategories}
                authAxios={authAxios}
                onChanged={load}
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-svj-border">
        {isPaid && (
          <button type="button" onClick={resendEmail} disabled={resending} data-testid="order-edit-resend-email" className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-svj-cyan/60 text-svj-cyan px-3 py-1.5 hover:bg-svj-cyan/10 disabled:opacity-50">
            {resending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Mail opnieuw versturen
          </button>
        )}
        {canSendPaymentLink && (
          <button type="button" onClick={sendPaymentLink} disabled={sendingLink} data-testid="order-edit-send-payment-link" className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-svj-orange/60 text-svj-orange px-3 py-1.5 hover:bg-svj-orange/10 disabled:opacity-50">
            {sendingLink ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Betaallink versturen
          </button>
        )}
        {canReservePending && (
          <button
            type="button"
            onClick={reservePending}
            disabled={reservingPending}
            data-testid="order-edit-reserve-pending"
            title="Blokkeert de plek opnieuw (tickettype/tijdslot) zonder een betaallink te versturen — gebruik dit om een bestelling waarvan de betaalsessie is verlopen weer in de wachtstand te zetten."
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-amber-500/60 text-amber-300 px-3 py-1.5 hover:bg-amber-500/10 disabled:opacity-50"
          >
            {reservingPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />} Zet in wachtstand (blokkeer, betaallink later)
          </button>
        )}
        {canMarkPaid && (
          <button type="button" onClick={markPaid} disabled={markingPaid} data-testid="order-edit-mark-paid" className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-emerald-500/60 text-emerald-300 px-3 py-1.5 hover:bg-emerald-500/10 disabled:opacity-50">
            {markingPaid ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />} Markeer als betaald
          </button>
        )}
        {canDeleteTickets && (
          <button type="button" onClick={deleteTickets} disabled={deletingTickets} data-testid="order-edit-delete-tickets" className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-red-500/60 text-red-300 px-3 py-1.5 hover:bg-red-500/10 disabled:opacity-50">
            {deletingTickets ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Verwijder tickets
          </button>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-svj-card border border-svj-border rounded-2xl p-5">
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="h-4 w-4 text-svj-cyan" />
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className="mt-3 text-3xl font-extrabold text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

export function AdminReportsOverview() {
  const { authAxios, session, me } = useAuth();
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ paid_orders: 0, tickets_sold: 0, revenue_cents: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authAxios.get("/admin/reports");
        setItems(data.items || []);
        setTotals(data.totals || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadExport = async () => {
    setExporting(true);
    try {
      const resp = await fetch(`${API_BASE}/admin/reports/export.xlsx`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "rapport-overzicht.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.message || "Export mislukt");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-reports-page">
      <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">Beheer</div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-white">Rapportage</h1>
      <p className="mt-1 text-sm text-slate-400">
        {me?.is_superadmin
          ? "Cijfers over alle organisaties."
          : `Cijfers van ${me?.organization?.name || "je organisatie"}.`}
      </p>

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={downloadExport}
          disabled={exporting || loading}
          data-testid="reports-export-overview"
          className="inline-flex items-center gap-2 rounded-full border border-svj-cyan/60 text-svj-cyan font-bold py-2 px-4 text-sm hover:bg-svj-cyan/10 transition-all disabled:opacity-50"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Excel downloaden
        </button>
      </div>

      {loading ? (
        <div className="mt-8 py-16 text-center text-slate-400">
          <Loader2 className="h-6 w-6 mx-auto animate-spin text-svj-cyan" />
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="reports-totals">
            <KPI icon={Receipt} label="Betaalde bestellingen" value={totals.paid_orders || 0} testid="kpi-paid-orders" />
            <KPI icon={Ticket} label="Tickets verkocht" value={totals.tickets_sold || 0} testid="kpi-tickets-sold" />
            <KPI icon={TrendingUp} label="Omzet" value={formatPrice(totals.revenue_cents || 0)} sub="inclusief btw" testid="kpi-revenue" />
          </div>

          <div className="mt-10 rounded-2xl border border-svj-border overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-svj-card/60 text-[11px] uppercase tracking-widest text-slate-400 font-semibold border-b border-svj-border">
              <div className="col-span-4">Event</div>
              <div className="col-span-2">Datum</div>
              <div className="col-span-2">Bestellingen</div>
              <div className="col-span-2">Tickets</div>
              <div className="col-span-2 text-right">Omzet</div>
            </div>
            {items.length === 0 && (
              <div className="p-10 text-center text-slate-400" data-testid="reports-empty">
                <BarChart3 className="h-8 w-8 text-svj-cyan mx-auto" />
                <div className="mt-3 text-white font-semibold text-sm">Nog geen data</div>
                <p className="mt-1 text-xs">Zodra er events + betalingen zijn zie je hier de cijfers.</p>
              </div>
            )}
            {items.map((ev) => (
              <Link
                to={`/admin/reports/${ev.id}`}
                key={ev.id}
                data-testid={`reports-row-${ev.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-4 items-center border-b border-svj-border last:border-b-0 bg-svj-card hover:bg-svj-bg/60 transition-colors"
              >
                <div className="md:col-span-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-svj-bg border border-svj-border overflow-hidden shrink-0">
                      {ev.cover_image_url && <img src={ev.cover_image_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white font-semibold truncate">{ev.title}</div>
                      <div className="text-xs text-slate-400 truncate">{ev.organizations?.name}</div>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2 text-sm text-slate-300 inline-flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4 text-slate-500" />
                  {formatEventDate(ev.starts_at)}
                </div>
                <div className="md:col-span-2 text-sm text-white font-semibold" data-testid={`reports-orders-${ev.id}`}>
                  {ev.stats?.paid_orders || 0}
                </div>
                <div className="md:col-span-2 text-sm text-white font-semibold">
                  {ev.stats?.tickets_sold || 0}
                </div>
                <div className="md:col-span-2 text-right text-svj-cyan font-extrabold" data-testid={`reports-revenue-${ev.id}`}>
                  {formatPrice(ev.stats?.revenue_cents || 0)}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function AdminReportDetail() {
  const { eventId } = useParams();
  const { authAxios, session, me } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showingAll, setShowingAll] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [sendingLinks, setSendingLinks] = useState(false);
  const [reservingSelected, setReservingSelected] = useState(false);

  const load = async (all = showingAll) => {
    try {
      const { data } = await authAxios.get(`/admin/reports/${eventId}`, {
        params: all ? { limit: 1000 } : undefined,
      });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAllOrders = async () => {
    setShowingAll(true);
    setLoading(true);
    await load(true);
  };

  const downloadExport = async () => {
    setExporting(true);
    try {
      const resp = await fetch(`${API_BASE}/admin/reports/${eventId}/export.xlsx`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const slug = data?.event?.slug || eventId;
      a.href = url; a.download = `rapport-${slug}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.message || "Export mislukt");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const resendEmail = async (orderId) => {
    setResending(orderId);
    try {
      await authAxios.post(`/admin/orders/${orderId}/resend-email`);
      toast.success("E-mail opnieuw verzonden");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setResending(null);
    }
  };

  const refundOrder = async (o) => {
    const amount = ((o.total_cents || 0) / 100).toLocaleString("nl-NL", { style: "currency", currency: "EUR" });
    const label = `${o.customer_first_name || ""} ${o.customer_last_name || ""}`.trim() || o.customer_email;
    if (!window.confirm(
      `Weet je zeker dat je deze bestelling wilt annuleren en refunden?\n\n` +
      `${label} — ${amount}\n\n` +
      `De tickets worden ongeldig, de capaciteit wordt vrijgegeven en de klant ontvangt een terugbetalings-mail.`,
    )) return;
    setRefunding(o.id);
    try {
      const { data } = await authAxios.post(`/admin/orders/${o.id}/refund`);
      if (data?.already_refunded) toast.info("Deze bestelling was al geannuleerd");
      else toast.success("Terugbetaling gestart");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setRefunding(null);
    }
  };

  const toggleSelected = (orderId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = (data?.recent_orders || []).map((o) => o.id);
    setSelected((prev) => {
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(visibleIds);
    });
  };

  const deleteSelected = async () => {
    const orderIds = Array.from(selected);
    if (orderIds.length === 0) return;
    const orders = (data?.recent_orders || []).filter((o) => selected.has(o.id));
    const totalCents = orders.reduce((sum, o) => sum + (o.total_cents || 0), 0);
    const amount = (totalCents / 100).toLocaleString("nl-NL", { style: "currency", currency: "EUR" });
    if (!window.confirm(
      `Je staat op het punt ${orderIds.length} bestelling(en) PERMANENT te verwijderen (totaal ${amount}).\n\n` +
      `LET OP: dit annuleert GEEN betaling bij de betaalprovider — gebruik dit alleen voor test-/simulatiebestellingen, ` +
      `nooit voor een echte betaalde bestelling die refund nodig heeft.\n\n` +
      `Tickets, QR-codes en alle gekoppelde data worden onherroepelijk verwijderd en de verkocht-teller gaat weer omlaag. Doorgaan?`,
    )) return;
    setDeleting(true);
    try {
      const { data: result } = await authAxios.post("/admin/orders/delete", {
        order_ids: orderIds,
        event_id: eventId,
      });
      const deletedCount = result?.deleted_count || 0;
      if (deletedCount > 0) toast.success(`${deletedCount} bestelling(en) verwijderd`);
      const problems = (result?.not_found?.length || 0) + (result?.wrong_event?.length || 0) + Object.keys(result?.errors || {}).length;
      if (problems > 0) toast.error(`${problems} bestelling(en) konden niet worden verwijderd`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setDeleting(false);
    }
  };

  // A "reserved" order was created via "Handmatig ticket aanmaken" ->
  // "Stuur betaallink" + "Betaallink later versturen" -- its ticket-type/
  // timeslot capacity is already held, but no payment link/e-mail has been
  // sent yet. Selectable by any event manager (not just a superadmin -- that
  // restriction is specific to the permanent bulk-delete action below), so
  // the admin who took several phone-in bookings can send every payment link
  // out together, at a moment of their own choosing.
  const sendSelectedPaymentLinks = async () => {
    const orders = (data?.recent_orders || []).filter((o) => selected.has(o.id) && o.status === "reserved");
    const orderIds = orders.map((o) => o.id);
    if (orderIds.length === 0) return;
    setSendingLinks(true);
    try {
      const { data: result } = await authAxios.post("/admin/orders/send-payment-links", { order_ids: orderIds });
      const sent = result?.sent_count || 0;
      const failed = result?.failed_count || 0;
      if (sent > 0) toast.success(`${sent} betaallink(en) verstuurd`);
      if (failed > 0) toast.error(`${failed} betaallink(en) konden niet worden verstuurd`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSendingLinks(false);
    }
  };

  // Recovers several stuck/expired orders (e.g. "Stuur betaallink" sessions
  // that timed out) back into a held "reserved" state in one go, without
  // re-entering each booking by hand. See admin_bulk_reserve_pending's own
  // docstring for why each order is processed independently.
  const reserveSelectedPending = async () => {
    const orderIds = (data?.recent_orders || [])
      .filter((o) => selected.has(o.id) && !o.paid && o.status !== "cancelled" && !["reserved", "payment_pending"].includes(o.status))
      .map((o) => o.id);
    if (orderIds.length === 0) return;
    setReservingSelected(true);
    try {
      const { data: result } = await authAxios.post("/admin/orders/reserve-pending", { order_ids: orderIds });
      const reserved = result?.reserved_count || 0;
      const failed = result?.failed_count || 0;
      if (reserved > 0) toast.success(`${reserved} bestelling(en) weer in de wachtstand gezet`);
      if (failed > 0) toast.error(`${failed} bestelling(en) konden niet in de wachtstand gezet worden`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setReservingSelected(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="py-24 text-center text-slate-400">
        <Loader2 className="h-6 w-6 mx-auto animate-spin text-svj-cyan" />
      </div>
    );
  }

  const { event, stats, breakdown, recent_orders } = data;
  const hasAnyReserved = (recent_orders || []).some((o) => o.status === "reserved");
  const selectedReservedCount = (recent_orders || []).filter((o) => selected.has(o.id) && o.status === "reserved").length;
  // A stuck/expired order (e.g. a "Stuur betaallink" session that timed out
  // without the customer paying) can be recovered back into a held
  // "reserved" state without re-entering the whole booking -- see
  // reserveSelectedPending. This client-side filter is just a sensible
  // pre-selection (unpaid, not cancelled, not already reserved/pending, a
  // real amount); the backend endpoint itself does the authoritative check
  // (including "no tickets exist yet") per order and reports any that don't
  // qualify, so over-selecting here never silently breaks something.
  const selectedRecoverableCount = (recent_orders || []).filter(
    (o) => selected.has(o.id) && !o.paid && o.status !== "cancelled" && !["reserved", "payment_pending"].includes(o.status) && (o.total_cents || 0) > 0,
  ).length;
  const showSelectionColumn = !!me?.is_superadmin || hasAnyReserved;
  const totalCapacity = breakdown.reduce((a, t) => a + (t.capacity ?? 0), 0);
  const utilisation = totalCapacity > 0 ? Math.round((stats.tickets_sold / totalCapacity) * 100) : null;

  return (
    <div data-testid="admin-report-detail">
      <Link to="/admin/reports" className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-4">
        <ArrowLeft className="h-4 w-4" /> Terug naar rapportage
      </Link>
      <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">
        {event.organizations?.name || "Event"}
      </div>
      <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold text-white">{event.title}</h1>
      <div className="mt-2 text-sm text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
        <span>{formatEventDate(event.starts_at)}</span>
        {event.location && <span>· {event.location}</span>}
        <span>· status: {event.status}</span>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Receipt} label="Betaalde bestellingen" value={stats.paid_orders} />
        <KPI icon={Ticket} label="Tickets verkocht" value={stats.tickets_sold} sub={utilisation != null ? `${utilisation}% van capaciteit` : "geen capaciteitslimiet"} />
        <KPI icon={TrendingUp} label="Omzet (incl.)" value={formatPrice(stats.revenue_cents)} sub={`btw: ${formatPrice(stats.vat_cents)}`} />
        <KPI icon={Users} label="Gem. per bestelling" value={stats.paid_orders ? formatPrice(Math.round(stats.revenue_cents / stats.paid_orders)) : "—"} />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 bg-svj-card border border-svj-border rounded-2xl p-5" data-testid="report-scan-panel">
          <div className="flex items-center gap-2 text-slate-400">
            <ScanLine className="h-4 w-4 text-svj-cyan" />
            <span className="text-[11px] uppercase tracking-widest font-semibold">Bij de deur gescand</span>
          </div>
          <div className="mt-3 flex items-end gap-6 flex-wrap">
            <div>
              <div className="text-3xl font-extrabold text-white" data-testid="report-scan-count">
                {stats.tickets_scanned || 0}
                <span className="text-lg text-slate-400 font-semibold"> / {stats.tickets_sold || 0}</span>
              </div>
              <div className="mt-1 text-xs text-slate-400">
                {stats.tickets_unscanned || 0} nog niet gescand
              </div>
            </div>
            <div className="flex-1 min-w-[160px]">
              <div className="h-2 bg-svj-bg border border-svj-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple transition-all"
                  style={{ width: `${Math.min(100, stats.scan_rate || 0)}%` }}
                />
              </div>
              <div className="mt-1 text-xs text-slate-400 text-right" data-testid="report-scan-rate">{stats.scan_rate || 0}%</div>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={downloadExport}
          disabled={exporting}
          data-testid="report-export-detail"
          className="lg:col-span-1 bg-svj-card border border-svj-cyan/50 text-svj-cyan rounded-2xl p-5 hover:bg-svj-cyan/5 transition-all disabled:opacity-50 flex flex-col items-start justify-center gap-2"
        >
          {exporting ? <Loader2 className="h-6 w-6 animate-spin" /> : <Download className="h-6 w-6" />}
          <div>
            <div className="text-sm font-bold">Excel export</div>
            <div className="text-[11px] text-slate-400">Overzicht · bestellingen · tickets</div>
          </div>
        </button>
      </div>

      <div className="mt-10">
        <SalesChart
          endpoint={`/admin/reports/${eventId}/sales-timeseries`}
          authAxios={authAxios}
          testid="report-sales-chart"
        />
      </div>

      <div className="mt-10">
        <div className="text-sm font-semibold text-white uppercase tracking-wider mb-3">Per tickettype</div>
        <div className="rounded-2xl border border-svj-border overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-svj-card/60 text-[11px] uppercase tracking-widest text-slate-400 font-semibold border-b border-svj-border">
            <div className="col-span-4">Type</div>
            <div className="col-span-2">Prijs</div>
            <div className="col-span-2">Verkocht</div>
            <div className="col-span-2">% van cap.</div>
            <div className="col-span-2 text-right">Omzet</div>
          </div>
          {breakdown.map((t) => {
            const util = t.capacity ? Math.round((t.sold_qty_paid / t.capacity) * 100) : null;
            return (
              <div key={t.id} data-testid={`report-breakdown-${t.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-4 bg-svj-card border-b border-svj-border last:border-b-0">
                <div className="md:col-span-4 text-white font-semibold">
                  {t.name}
                  {!t.active && <span className="ml-2 text-[10px] uppercase tracking-widest bg-svj-border text-slate-400 rounded-full px-2 py-0.5">Inactief</span>}
                </div>
                <div className="md:col-span-2 text-sm text-slate-300">{formatPrice(t.price_cents)}</div>
                <div className="md:col-span-2 text-sm text-white font-semibold">
                  {t.sold_qty_paid} <span className="text-slate-400">/ {t.capacity ?? "∞"}</span>
                </div>
                <div className="md:col-span-2 text-sm">
                  {util != null ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-svj-bg border border-svj-border rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple" style={{ width: `${Math.min(100, util)}%` }} />
                      </div>
                      <span className="text-xs text-slate-400 w-9 text-right">{util}%</span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">n.v.t.</span>
                  )}
                </div>
                <div className="md:col-span-2 text-right text-svj-cyan font-bold">{formatPrice(t.revenue_cents)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="text-sm font-semibold text-white uppercase tracking-wider">
            Bestellingen
            {data?.total_orders != null && (
              <span className="ml-2 text-slate-400 normal-case font-normal">
                ({(recent_orders || []).length} van {data.total_orders})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!showingAll && (data?.total_orders || 0) > (recent_orders || []).length && (
              <button
                type="button"
                onClick={loadAllOrders}
                data-testid="report-show-all-orders"
                className="text-xs rounded-full border border-svj-border text-slate-300 px-3 py-1.5 hover:bg-svj-bg/60"
              >
                Toon alle bestellingen
              </button>
            )}
            {selectedReservedCount > 0 && (
              <button
                type="button"
                disabled={sendingLinks}
                onClick={sendSelectedPaymentLinks}
                data-testid="report-send-selected-payment-links"
                className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full border border-svj-orange/60 text-svj-orange px-3 py-1.5 hover:bg-svj-orange/10 disabled:opacity-50"
              >
                {sendingLinks ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Verstuur betaallinks ({selectedReservedCount})
              </button>
            )}
            {selectedRecoverableCount > 0 && (
              <button
                type="button"
                disabled={reservingSelected}
                onClick={reserveSelectedPending}
                data-testid="report-reserve-selected-pending"
                title="Blokkeert de plek(ken) opnieuw zonder een betaallink te versturen — voor bestellingen waarvan de betaalsessie is verlopen."
                className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full border border-amber-500/60 text-amber-300 px-3 py-1.5 hover:bg-amber-500/10 disabled:opacity-50"
              >
                {reservingSelected ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />}
                Zet in wachtstand ({selectedRecoverableCount})
              </button>
            )}
            {me?.is_superadmin && selected.size > 0 && (
              <button
                type="button"
                disabled={deleting}
                onClick={deleteSelected}
                data-testid="report-delete-selected"
                className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full border border-red-500/60 text-red-300 px-3 py-1.5 hover:bg-red-500/10 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Verwijder ({selected.size}) — geen refund
              </button>
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card">
          {(recent_orders || []).length === 0 && (
            <div className="p-8 text-center text-sm text-slate-400" data-testid="report-recent-empty">Nog geen bestellingen.</div>
          )}
          {showSelectionColumn && (recent_orders || []).length > 0 && (
            <div className="flex items-center gap-2 px-5 py-2 border-b border-svj-border bg-svj-bg/30 text-[11px] text-slate-400">
              <input
                type="checkbox"
                data-testid="report-select-all"
                checked={(recent_orders || []).length > 0 && (recent_orders || []).every((o) => selected.has(o.id))}
                onChange={toggleSelectAllVisible}
                className="h-3.5 w-3.5 rounded border-svj-border"
              />
              Selecteer alles zichtbaar
            </div>
          )}
          {(recent_orders || []).map((o) => (
            <Fragment key={o.id}>
            <div className="flex items-center gap-3 px-5 py-3 border-b border-svj-border last:border-b-0" data-testid={`report-order-${o.id}`}>
              {(me?.is_superadmin || o.status === "reserved") && (
                <input
                  type="checkbox"
                  data-testid={`report-select-${o.id}`}
                  checked={selected.has(o.id)}
                  onChange={() => toggleSelected(o.id)}
                  className="h-3.5 w-3.5 rounded border-svj-border shrink-0"
                />
              )}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 text-sm items-center flex-1 min-w-0">
              <div className="md:col-span-3 text-white truncate">{o.customer_first_name} {o.customer_last_name}</div>
              <div className="md:col-span-3 text-slate-400 truncate">{o.customer_email}</div>
              <div className="md:col-span-2">
                {(() => {
                  const refunded = o.status === "cancelled" || (o.pay_status || "").toUpperCase() === "REFUNDED";
                  const paid = !refunded && (o.paid || o.status === "paid");
                  const failed = !refunded && !paid && (o.status === "failed");
                  const reserved = !refunded && !paid && !failed && o.status === "reserved";
                  return (
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                      refunded
                        ? "bg-slate-500/15 border-slate-500/40 text-slate-300"
                        : paid
                        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                        : failed
                        ? "bg-red-500/15 border-red-500/40 text-red-300"
                        : reserved
                        ? "bg-svj-purple/15 border-svj-purple/40 text-svj-purple"
                        : "bg-svj-orange/15 border-svj-orange/40 text-svj-orange"
                    }`}>
                      {refunded ? "Terugbetaald" : paid ? "Betaald" : reserved ? "Gereserveerd" : o.status}
                    </span>
                  );
                })()}
              </div>
              <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
                {(o.paid || o.status === "paid") && o.status !== "cancelled" && (o.pay_status || "").toUpperCase() !== "REFUNDED" && (
                  <>
                    {o.email_status === "sent" && (
                      <span title={`Verstuurd op ${o.email_sent_at || ""}`} className="inline-flex items-center gap-1 text-emerald-400 text-[11px]"><Mail className="h-3.5 w-3.5" /> mail ok</span>
                    )}
                    {o.email_status === "failed" && (
                      <>
                        <span title={o.email_last_error || "Onbekende fout"} className="inline-flex items-center gap-1 text-red-400 text-[11px]" data-testid={`report-mail-failed-${o.id}`}><MailX className="h-3.5 w-3.5" /> mail faalde ({o.email_attempts || 0})</span>
                        <button type="button" disabled={resending === o.id} onClick={() => resendEmail(o.id)} data-testid={`report-resend-${o.id}`} className="text-[11px] rounded-full border border-svj-cyan/60 text-svj-cyan px-2 py-0.5 hover:bg-svj-cyan/10">
                          {resending === o.id ? <Loader2 className="h-3 w-3 animate-spin inline" /> : "Opnieuw"}
                        </button>
                      </>
                    )}
                    {!o.email_status && (
                      <span className="inline-flex items-center gap-1 text-slate-500 text-[11px]"><Mail className="h-3.5 w-3.5" /> —</span>
                    )}
                    {o.status !== "cancelled" && (o.pay_status || "").toUpperCase() !== "REFUNDED" && (
                      <button
                        type="button"
                        disabled={refunding === o.id}
                        onClick={() => refundOrder(o)}
                        data-testid={`report-refund-${o.id}`}
                        title="Annuleer & refund via Pay.nl"
                        className="inline-flex items-center gap-1 text-[11px] rounded-full border border-red-500/50 text-red-300 px-2 py-0.5 hover:bg-red-500/10 disabled:opacity-50"
                      >
                        {refunding === o.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                        Refund
                      </button>
                    )}
                  </>
                )}
                {(o.status === "cancelled" || (o.pay_status || "").toUpperCase() === "REFUNDED") && (
                  <span data-testid={`report-refunded-${o.id}`} className="inline-flex items-center gap-1 text-[11px] rounded-full border border-slate-500/40 text-slate-400 px-2 py-0.5">
                    <RotateCcw className="h-3 w-3" /> Terugbetaald
                  </span>
                )}
              </div>
              <div className="md:col-span-2 text-right text-white font-semibold flex items-center justify-end gap-2">
                {formatPrice(o.total_cents)}
                <button
                  type="button"
                  onClick={() => setEditingOrderId(editingOrderId === o.id ? null : o.id)}
                  data-testid={`report-edit-order-${o.id}`}
                  title="Bestelling bewerken"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-svj-border text-slate-300 hover:text-white hover:border-svj-cyan/60 shrink-0"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            </div>
            {editingOrderId === o.id && (
              <OrderEditPanel orderId={o.id} authAxios={authAxios} onClose={() => setEditingOrderId(null)} />
            )}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
